import * as React from "react";

/**
 * Shared inline SVG glyphs for the whole component. Size/stroke/colour come from each
 * consuming component's CSS (descendant `svg` selectors); icons render a bare `<svg>`.
 * Keep every icon here so there's a single source of truth and no per-file duplication.
 */

/* ── Attachments / files ── */

export const FileGlyph = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
	</svg>
);

export const ImageGlyph = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<rect x="3" y="4" width="18" height="16" rx="2" />
		<circle cx="8.5" cy="9.5" r="1.5" />
		<path d="M21 16l-5-5L5 20" />
	</svg>
);

export const Paperclip = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
	</svg>
);

export const Shield = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M12 22s-8-4-8-10V5l8-3 8 3v7c0 6-8 10-8 10z" />
	</svg>
);

/** Files-list icon (header action) — distinct from the single-document FileGlyph. */
export const FilesIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M14 3v5h5M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
		<path d="M8 13h8M8 17h5" />
	</svg>
);

/* ── Sidebar / navigation ── */

/** Sidebar/panel toggle mark (header toggle, sidebar collapse button, and the rail). */
export const PanelIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<rect x="3" y="4" width="18" height="16" rx="2" />
		<path d="M9 4v16" />
	</svg>
);

export const NewChatIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
	</svg>
);

export const SearchIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<circle cx="11" cy="11" r="7" />
		<path d="M21 21l-4.3-4.3" />
	</svg>
);

/** Three-dot overflow trigger. */
export const Kebab = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<circle cx="12" cy="5" r="1.6" />
		<circle cx="12" cy="12" r="1.6" />
		<circle cx="12" cy="19" r="1.6" />
	</svg>
);

export const Trash = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
	</svg>
);

// Star & Pencil — used by the (currently hidden) Favourite/Rename menu items; kept here so
// re-enabling those is just an import. See SessionMenu.
export const Star = ({ filled }: { filled?: boolean }): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden style={filled ? { fill: "currentColor", stroke: "currentColor" } : undefined}>
		<path d="M12 3l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 18.8 6.2 21.9l1.1-6.5L2.6 9.8l6.5-.9z" />
	</svg>
);

export const Pencil = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
	</svg>
);

/* ── Composer ── */

export const Plus = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M12 5v14M5 12h14" />
	</svg>
);

export const ArrowUp = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M12 19V5M5 12l7-7 7 7" />
	</svg>
);

/** Stop/abort glyph shown on the send button while generating. */
export const Square = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<rect x="6" y="6" width="12" height="12" rx="1" />
	</svg>
);

export const Mic = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3zM5 10v1a7 7 0 0 0 14 0v-1M12 19v3" />
	</svg>
);

/** Composer expand-to-fullscreen (diagonal out-arrows). */
export const ComposerExpandIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M9 15l-5 5M4 20h5M4 20v-5M15 9l5-5M20 4h-5M20 4v5" />
	</svg>
);

/** Composer collapse-from-fullscreen (diagonal in-arrows). */
export const ComposerCollapseIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M20 9h-5M15 9V4M15 9l5-5M4 15h5M9 15v5M9 15l-5 5" />
	</svg>
);

/** Uploading spinner — accepts a className so the consumer applies its spin animation. */
export const Spinner = ({ className }: { className?: string }): React.ReactElement => (
	<svg className={className} viewBox="0 0 24 24" aria-hidden>
		<circle cx="12" cy="12" r="9" />
	</svg>
);

/* ── Menus / header controls ── */

export const ChevronDown = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M6 9l6 6 6-6" />
	</svg>
);

export const Check = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M5 13l4 4L19 7" />
	</svg>
);

/* ── Agent trace step status ── */

export const CheckCircle = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<circle cx="12" cy="12" r="10" />
		<path d="M7.5 12.5l3 3 6-6.5" />
	</svg>
);

export const XCircle = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<circle cx="12" cy="12" r="10" />
		<path d="M9 9l6 6M15 9l-6 6" />
	</svg>
);

export const Ring = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<circle cx="12" cy="12" r="8" />
	</svg>
);

export const CloseIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M6 6l12 12M18 6L6 18" />
	</svg>
);

/** Enter-fullscreen (frame corners pointing out). */
export const FullscreenEnterIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M21 16v3a2 2 0 0 1-2 2h-3M8 21H5a2 2 0 0 1-2-2v-3" />
	</svg>
);

/** Exit-fullscreen (frame corners pointing in). */
export const FullscreenExitIcon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M16 21v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
	</svg>
);

export const Sun = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<circle cx="12" cy="12" r="4" />
		<path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
	</svg>
);

export const Moon = (): React.ReactElement => (
	<svg viewBox="0 0 24 24" aria-hidden>
		<path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
	</svg>
);

/* ── Brand assets ──
 * Plexie logo + icon-mark, used as sidebar fallbacks when the consumer supplies no
 * `sidebar.logoUrl` / `sidebar.markUrl`. The logo is a gradient wordmark (not a
 * currentColor glyph), so it lives as a self-contained SVG rendered via <img> — a
 * data URI keeps rendering identical to the previous static asset and stays SSR-safe. */
const PLEXIE_LOGO_SVG = `<svg width="304" height="53" viewBox="0 0 304 53" fill="none" xmlns="http://www.w3.org/2000/svg"><g filter="url(#filter0_n_3699_7012)"><path d="M194.938 19.2793H197.919L210.499 0H227.85L210.394 26.2207L227.857 52.3809H210.439L197.918 33.168H194.939L182.418 52.3809H165L182.464 26.2207L165.008 0H182.358L194.938 19.2793ZM37.6055 2.61914C42.3432 2.61914 46.363 3.81766 49.3252 6.5332C52.4135 9.32729 53.749 13.4087 53.749 18.292C53.749 23.1753 52.4135 27.2567 49.3252 30.0508C46.363 32.7663 42.3432 33.9648 37.6055 33.9648H14.1758V49.4297H0V2.61914H37.6055ZM73.2314 37.0176H103.984V49.4297H59.0557V2.61914H73.2314V37.0176ZM159.984 15.0312H123.292V19.5537H144.048V31.9658H123.292V37.0176H159.984V49.4297H109.116V2.61914H159.984V15.0312ZM247.649 49.4297H233.473V2.61914H247.649V49.4297ZM303.81 15.0312H267.117V19.5537H287.44V31.9658H267.117V37.0176H303.81V49.4297H252.941V2.61914H303.81V15.0312ZM14.1758 15.0312V21.5L36.4883 21.5527H36.4912C37.8378 21.5523 38.4687 21.2188 38.7803 20.9209L38.8174 20.8857C39.0376 20.6838 39.4561 20.0515 39.4561 18.292C39.4561 16.4619 39.0239 15.8792 38.8584 15.7344L38.8184 15.7002L38.7803 15.6631C38.4685 15.365 37.8366 15.0312 36.4883 15.0312H14.1758Z" fill="url(#paint0_linear_3699_7012)"/></g><defs><filter id="filter0_n_3699_7012" x="0" y="0" width="303.81" height="52.3809" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feTurbulence type="fractalNoise" baseFrequency="2 2" stitchTiles="stitch" numOctaves="3" result="noise" seed="1888"/><feColorMatrix in="noise" type="luminanceToAlpha" result="alphaNoise"/><feComponentTransfer in="alphaNoise" result="coloredNoise1"><feFuncA type="discrete" tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "/></feComponentTransfer><feComposite operator="in" in2="shape" in="coloredNoise1" result="noise1Clipped"/><feFlood flood-color="rgba(0, 0, 0, 0.11)" result="color1Flood"/><feComposite operator="in" in2="noise1Clipped" in="color1Flood" result="color1"/><feMerge result="effect1_noise_3699_7012"><feMergeNode in="shape"/><feMergeNode in="color1"/></feMerge></filter><linearGradient id="paint0_linear_3699_7012" x1="-18.4855" y1="49.7363" x2="231.351" y2="-122.343" gradientUnits="userSpaceOnUse"><stop stop-color="#39E8C6"/><stop offset="0.28526" stop-color="#1DC4F9"/><stop offset="0.693003" stop-color="#FB61DA"/><stop offset="1" stop-color="#F5DC54"/></linearGradient></defs></svg>`;

/** Plexie wordmark — sidebar logo fallback when the consumer provides no `sidebar.logoUrl`. */
export const PLEXIE_LOGO_URL = `data:image/svg+xml,${encodeURIComponent(PLEXIE_LOGO_SVG)}`;

/** Plexie icon-mark — collapsed-rail mark fallback when no `sidebar.markUrl` is provided. */
export const PLEXIE_MARK_URL = "https://cdn-fastly.parrotanalytics.com/general-assets/plexie-icon-v3.png";
